// ┌──────────────────────────────────────────────┐
// │ Cart — Protection Plan Offer (E20-1832)        │
// └──────────────────────────────────────────────┘
// The plan offer on a machine's cart line, drawer and cart page alike
// (snippets/bfx-protection-plan-cart-offer.liquid).
//
// Details opens the same coverage modal the PDP uses. It isn't rendered into the cart — it is
// ~20KB per eligible line, and the drawer fetches the cart markup on every page — so the first
// click fetches it for that product (sections/bfx-protection-plan-cart-modal.liquid), inserts it
// into <body> and wires it through bfx-protection-plan-modal.js. Later clicks reuse it.
//
// The drawer stays open underneath. Impulse assumes one overlay at a time, so four of its
// behaviours are handled while this modal is up — see Drawer guards below and insertModal.

{
  const SECTION_ID = 'bfx-protection-plan-cart-modal';
  const ESCAPE = 27;

  // Modal load per product URL, shared by every line and both surfaces.
  const modals = new Map();
  // The modal currently open from an offer, and the Details button that opened it.
  let current = null;
  // True only for the synchronous span of open() — see the modalOpen guard.
  let opening = false;
  // The latest Details click still waiting on its modal. Two can be loading at once (another
  // line's Details pressed before the first arrived), and only the latest opens: a second
  // theme.Modals opened over the first would overwrite `current` and strand the first's trap.
  let requested = null;

  const cartDrawer = () => document.getElementById('CartDrawer');
  const drawerIsOpen = () => cartDrawer()?.classList.contains('drawer--is-open') ?? false;

  // ── Drawer guards ───────────────────────────

  // Every Impulse drawer closes itself on the global modalOpen event. Swallowed only while one
  // of these modals is opening, so any other modal still closes the drawer as it always did.
  // Capture on window runs before the drawers' listeners on document.
  window.addEventListener(
    'modalOpen',
    (event) => {
      if (opening) event.stopImmediatePropagation();
    },
    true
  );

  // The drawer and the modal both close on Esc from window listeners, so one press would close
  // both. Taken first, in the capture phase, and applied to the modal alone.
  window.addEventListener(
    'keyup',
    (event) => {
      if (!current || event.keyCode !== ESCAPE) return;
      event.stopImmediatePropagation();
      current.instance.close();
    },
    true
  );

  // Put focus back in the drawer and re-trap it against the drawer as it is now. Opening the
  // modal took focus out of the drawer, and the cart may have re-rendered while it was up.
  // Same re-arm bfx-mobile-nav.js uses.
  const retrapDrawer = (opener) => {
    if (!drawerIsOpen()) return;
    window.theme.a11y.trapFocus({
      container: cartDrawer(),
      namespace: 'drawer_focus',
      // The opener is gone if the cart re-rendered while the modal was up.
      elementToFocus: opener?.isConnected ? opener : cartDrawer(),
    });
  };

  // ── Modal loading ───────────────────────────

  const insertModal = (html) => {
    // Only the modal element is taken. The snippet's inline script would never run from
    // here anyway; init() below does what it does.
    const modal = new DOMParser().parseFromString(html, 'text/html').querySelector('.modal');
    if (!modal) throw new Error('the section rendered no modal');

    // Lifts it over the cart drawer; see the modifier in _protection-plan-cart-offer.scss.
    modal.classList.add('modal--over-cart');
    document.body.append(modal);

    // The drawer closes on any click outside itself, and this modal lives outside it.
    // Stopping here still lets the modal's own click-outside handler on this element run.
    modal.addEventListener('click', (event) => event.stopPropagation());

    document.addEventListener(`modalClose.${modal.id}`, () => {
      const opener = current?.opener;
      current = null;
      retrapDrawer(opener);
    });

    const instance = window.bfxProtectionPlanModal.init(modal.id);
    if (!instance) throw new Error(`could not wire modal ${modal.id}`);
    return instance;
  };

  const loadModal = (productUrl) => {
    if (!modals.has(productUrl)) {
      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('section_id', SECTION_ID);

      const request = fetch(url).then((response) => {
        if (!response.ok) throw new Error(`section request failed (${response.status})`);
        return response.text().then(insertModal);
      });
      // A failed load isn't cached, so the next click tries again.
      request.catch(() => modals.delete(productUrl));
      modals.set(productUrl, request);
    }
    return modals.get(productUrl);
  };

  const openModal = (instance, opener) => {
    current = { instance, opener };
    opening = true;
    try {
      // theme.Modals reads the opener off the event to set aria-expanded and return focus on
      // close. The click has long finished dispatching by the time the modal has loaded, so its
      // currentTarget is gone; hand over an event-shaped object naming the button instead.
      instance.open({ currentTarget: opener, preventDefault() {}, stopPropagation() {} });
    } finally {
      opening = false;
    }
  };

  // ── Adding a plan ───────────────────────────
  //
  // Picking a radio only chooses a plan and reveals Add; Add adds it, bundled to the line's
  // machine with the same properties the PDP attach writes (see
  // docs/dev/architecture/protection-plan-order-data-contract.md). A bundle is always ONE machine
  // and one plan (protection-plan-frontend.md § A bundle's quantity is fixed), so:
  //   • qty 1  — stamp the existing machine line with the bundle, then add the plan into it. The
  //              line keeps its place, and nothing re-checks the machine's inventory, so this
  //              works on the last unit in stock.
  //   • qty 2+ — split one unit off: add a qty-1 machine and the plan as their own bundle (the
  //              PDP's payload), then drop the original line by one. The rest stay an ordinary
  //              line that still carries the offer.
  // Neither path ever leaves a plan without its machine: the split adds both in one request, and
  // the qty-1 path pairs the machine before the plan exists. Checkout is held and the rest of the
  // cart locked while either runs (see addPlan).
  // Once paired, cart-item.liquid drops the offer and the machine's stepper; the reconcile in
  // bfx-cart-protection-plan.js owns the bundle from then on.

  // One add at a time across the cart: each resolves lines against a cart the other would be
  // changing underneath it.
  let adding = false;

  // A failure that changed the cart re-renders it, and the render replaces the offer the message
  // was written into (a reconcile of its own, off another event, can re-render it again). So the
  // message, and the plan that was picked, are held for a few seconds and restored by whichever
  // offer for that machine connects: the shopper sees what went wrong and can retry in one press.
  const HELD_ERROR_MS = 8000;
  let heldError = null;
  // A quantity change the theme has sent but not finished: it marks the line's selector
  // is-loading until the response lands, then starts a cart rebuild and fires cart:updated without
  // waiting for it (theme.js, CartForm.quantityChanged). Any line counts, since a change reshuffles
  // line keys.
  const quantityPending = (surface) => !!surface?.querySelector('.js-qty__wrapper.is-loading');

  // Resolves true once the rebuild that change set off has replaced the lines, false after a few
  // seconds without it, so a lost request can't hold an add forever. False means the change may
  // still be in flight, and its response could land mid-add, so the caller stops rather than add.
  // Not on cart:updated: that fires as the rebuild's fetch starts, so a lock taken then would
  // freeze lines about to be swapped for live ones. The theme empties and refills the same
  // [data-products] element, so a childList mutation that leaves no change pending is the signal.
  const quantitySettled = (surface) =>
    new Promise((resolve) => {
      let timer = null;
      const observer = new MutationObserver(() => {
        if (quantityPending(surface)) return;
        clearTimeout(timer);
        observer.disconnect();
        resolve(true);
      });
      observer.observe(surface, { childList: true });
      timer = setTimeout(() => {
        observer.disconnect();
        resolve(false);
      }, 5000);
    });

  // Keyed like findMachine's identity, the variant plus the line's properties, so the message
  // lands on the machine it was about and not on another line of the same variant.
  const holdError = (variantId, properties, message, planId) => {
    heldError = {
      variantId: String(variantId),
      properties: propertiesKey(properties),
      message,
      planId: planId === null ? null : String(planId),
      until: Date.now() + HELD_ERROR_MS,
    };
  };

  const addItems = (items) =>
    fetch(`${window.bfxCart.root()}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ items }),
    });

  // What an add whose response never arrived left behind — Shopify can accept the POST before the
  // connection drops. 'both' when the machine and its plan are there, 'none' when neither is,
  // 'partial' when only one is (the cart changed elsewhere, say the machine was removed in another
  // window), 'unknown' when the cart can't be read either. Only 'both' is a finished add.
  const bundleStateIn = (cart, bundleId) => {
    if (!Array.isArray(cart?.items)) return 'unknown';
    const lines = cart.items.filter((item) => item.properties?._bundle_id === bundleId);
    const plan = lines.some((item) => item.properties?._parent_sku);
    const machine = lines.some((item) => !item.properties?._parent_sku);
    if (plan && machine) return 'both';
    return plan || machine ? 'partial' : 'none';
  };

  const bundleState = async (bundleId) => {
    const cart = await window.bfxCart.getCart().catch((error) => {
      console.warn('Protection plan offer: could not read the cart to check the bundle', error);
      return null;
    });
    return bundleStateIn(cart, bundleId);
  };

  // A refused add. Shopify's reason goes to the console only: it answers every refusal with a
  // 422, the sold-out kind and the developer-facing kind ("Cannot find variant", for a plan that
  // has been unpublished) alike, so there's no telling which one a shopper could act on.
  const refusal = async (response) => {
    // A refusal whose body won't parse is still a refusal; its status is logged below either way.
    const data = await response.json().catch(() => null);
    const reason = data?.description || data?.message || '';
    return new AddError(`add.js failed (${response.status}): ${reason}`);
  };

  // An error that knows whether the rendered cart is out of date — this add changed it part-way,
  // or it had already changed elsewhere — so the caller re-renders rather than leave the shopper
  // looking at a cart that isn't theirs any more.
  class AddError extends Error {
    constructor(
      message,
      { cartChanged = false, planAdded = false, unconfirmed = false, resync = false } = {}
    ) {
      super(message);
      this.cartChanged = cartChanged;
      // A plan was left in the cart without its machine and the reconcile couldn't remove it, so
      // the caller reloads rather than re-render: the lock, checkout included, holds until the page
      // goes, and the page-load reconcile tries the removal again.
      this.resync = resync;
      // The plan is in the cart but the add didn't finish around it (the split's original line
      // kept the moved unit). Saying "couldn't add" would invite a retry that adds a second one.
      this.planAdded = planAdded;
      // Whether the plan landed can't be told (the add's response and the check after it were
      // both lost), so the shopper is asked to look rather than invited to retry.
      this.unconfirmed = unconfirmed;
    }
  }

  // A line's properties in a comparable form: the same set, whatever order they arrived in.
  const propertiesKey = (properties) =>
    JSON.stringify(Object.entries(properties || {}).sort(([a], [b]) => a.localeCompare(b)));

  // The machine line this offer sits on, in a fresh cart. By the rendered key when that key is
  // still there, otherwise by identity — a key is recomputed whenever a line's properties or
  // discount allocations change. Identity is the variant plus the line's properties, because two
  // lines of one variant can differ only by those (an engraving, a gift note), and it must settle
  // on exactly one line: an ambiguous match is treated as the cart having changed. Either way it
  // must be a machine with no plan paired to it, which also keeps the lookup from landing on a
  // bundle this add has just created.
  const findMachine = (cart, { key, variantId, properties }) => {
    const paired = new Set(
      cart.items
        .filter((item) => item.properties?._parent_sku && item.properties?._bundle_id)
        .map((item) => item.properties._bundle_id)
    );
    const unpaired = (item) =>
      !item.properties?._parent_sku && !paired.has(item.properties?._bundle_id);

    const byKey = cart.items.find((item) => item.key === key && unpaired(item));
    if (byKey) return byKey;

    const wanted = properties === undefined ? null : propertiesKey(properties);
    const matches = cart.items.filter(
      (item) =>
        String(item.variant_id) === String(variantId) &&
        unpaired(item) &&
        (wanted === null || propertiesKey(item.properties) === wanted)
    );
    return matches.length === 1 ? matches[0] : undefined;
  };

  const planLine = (planVariantId, bundleId, parentSku) => ({
    id: Number(planVariantId),
    quantity: 1,
    properties: {
      _bundle_id: bundleId,
      _parent_sku: parentSku,
      _protect_plan_for_assoc_product: 'true',
    },
  });

  // The machine side of a bundle. Its other properties (an engraving, a gift note) ride along; any
  // bundle properties left over from a plan removed earlier are replaced.
  const machineProperties = (machine, bundleId) => ({
    ...machine.properties,
    _bundle_id: bundleId,
    _has_assoc_protection_plan: 'true',
  });

  // The reconcile (bfx-cart-protection-plan.js) rebuilds a machine carrying bundle properties
  // with no plan as a clean line. Awaited, and without a render of its own, so the one recovery
  // render the caller asks for shows the cart already settled and the lock holds until then.
  // cart:updated reaches it the old way if that script isn't loaded.
  const reconcile = async () => {
    if (window.bfxCart.reconcile) {
      await window.bfxCart.reconcile();
      return;
    }
    document.dispatchEvent(new CustomEvent('cart:updated'));
  };

  // After a plan has landed: reconcile, then report whether this bundle's plan is still in the
  // cart without its machine. The reconcile logs a failed removal and carries on, so awaiting it
  // doesn't mean the plan went, and a plan with nothing to protect must not reach checkout. A cart
  // that can't be read can't be cleared either, so it counts as orphaned: the reload costs little
  // in a rare failure, and the page it loads renders the cart from the server.
  const reconcileLeavesOrphan = async (bundleId) => {
    await reconcile();
    const cart = await window.bfxCart.getCart().catch((error) => {
      console.warn(
        'Protection plan offer: could not read the cart to check for an orphaned plan',
        error
      );
      return null;
    });
    if (!Array.isArray(cart?.items)) return true;
    const lines = cart.items.filter((item) => item.properties?._bundle_id === bundleId);
    return (
      lines.some((item) => item.properties?._parent_sku) &&
      !lines.some((item) => !item.properties?._parent_sku)
    );
  };

  // qty 1: pair the existing line with a new bundle, then add the plan into it.
  //
  // Machine first, so there is never a moment when the cart holds a plan without its machine:
  // that state carries _protect_plan_for_assoc_product with nothing to protect, and the order
  // contract counts on it never reaching SAP. The reverse — a machine flagged for a plan it
  // doesn't have — is the state the contract already documents and tolerates (§ Relationship
  // flags), and the reconcile cleans it up. Stamping hits no inventory check, so this still works
  // on the last unit in stock.
  const attachToLine = async (machine, planVariantId, parentSku) => {
    const bundleId = window.bfxCart.bundleId();

    const pair = async (machine) => {
      try {
        return await window.bfxCart.changeLine(
          machine.key,
          machine.quantity,
          machineProperties(machine, bundleId)
        );
      } catch (error) {
        // No telling whether the change landed. If it did, the machine is flagged for a plan it
        // won't get, so the reconcile cleans it up; either way, re-render to show which it was.
        await reconcile();
        throw new AddError(`pairing request failed: ${error.message}`, { cartChanged: true });
      }
    };

    const stamped = await pair(machine);
    if (!stamped.ok) throw new AddError(`could not pair the machine (${stamped.status})`);

    let added = null;
    try {
      added = await addItems([planLine(planVariantId, bundleId, parentSku)]);
    } catch (error) {
      console.error('Protection plan offer: plan request failed', error);
      const state = await bundleState(bundleId);
      if (state === 'both') return bundleId;
      if (state === 'unknown') {
        // The plan may well have landed, so say so, as the split does, rather than invite a retry.
        const resync = await reconcileLeavesOrphan(bundleId);
        throw new AddError(`plan request failed, cart unreadable: ${error.message}`, {
          cartChanged: true,
          unconfirmed: true,
          resync,
        });
      }
    }
    if (added?.ok) {
      // A landed plan isn't a finished add on its own: the machine can have been removed in
      // another window since the stamp, leaving the plan with nothing to protect. The reconcile
      // removes a plan whose machine is gone.
      const state = await bundleState(bundleId);
      if (state === 'both') return bundleId;
      const resync = await reconcileLeavesOrphan(bundleId);
      throw new AddError(`plan added but the bundle is ${state}`, {
        cartChanged: true,
        unconfirmed: state === 'unknown',
        resync,
      });
    }

    // The machine is flagged for a plan that didn't arrive. Hand it to the reconcile rather than
    // leave it claiming a warranty; if even that can't finish, the contract reads a flag with no
    // matching plan line as no plan. Checked for an orphan too: on a request that got no answer,
    // the plan can have landed after the machine was removed elsewhere.
    const resync = await reconcileLeavesOrphan(bundleId);
    const error = added ? await refusal(added) : new AddError('plan request failed');
    error.cartChanged = true;
    error.resync = resync;
    throw error;
  };

  // qty 2+: move one unit into a bundle of its own.
  const splitOffBundle = async (machine, planVariantId, parentSku) => {
    const bundleId = window.bfxCart.bundleId();

    let added = null;
    try {
      added = await addItems([
        { id: machine.variant_id, quantity: 1, properties: machineProperties(machine, bundleId) },
        planLine(planVariantId, bundleId, parentSku),
      ]);
    } catch (error) {
      // No answer, but the bundle may be in the cart anyway: if it is, finish the split below so
      // a retry can't add a second one; if it isn't, nothing changed.
      const state = await bundleState(bundleId);
      if (state === 'partial') {
        // Half a bundle means the cart changed elsewhere; the reconcile removes a plan left
        // without its machine, and the shopper sees the cart as it is.
        const resync = await reconcileLeavesOrphan(bundleId);
        throw new AddError(`split left a partial bundle: ${error.message}`, {
          cartChanged: true,
          resync,
        });
      }
      if (state === 'unknown') {
        // Unreadable too, so it may well be there, and half of it may since have gone in another
        // window: the orphan check before any render, then no one-press retry.
        const resync = await reconcileLeavesOrphan(bundleId);
        throw new AddError(`split request failed, cart unreadable: ${error.message}`, {
          cartChanged: true,
          unconfirmed: true,
          resync,
        });
      }
      if (state !== 'both') {
        // Neither half is there, so nothing changed.
        throw new AddError(`split request failed: ${error.message}`);
      }
    }
    if (added && !added.ok) throw await refusal(added);

    // Now drop the original by the unit that moved. Re-resolved per attempt, because the add can
    // recompute keys, and a reduction hits no validation — only a network fault fails it. Only
    // ever from the quantity the split started with: if the line changed in the meantime (raised
    // in another window, say), writing started-minus-one would silently undo that, so it stops and
    // tells the shopper to check the quantity instead.
    //
    // A landed bundle isn't a finished split on its own, as on the qty-1 path: another window can
    // remove either half before the original is reduced, and reducing it then would take a unit
    // the shopper still wants or leave a plan with nothing to protect. So each attempt checks the
    // bundle in the cart it reads before reducing; anything but both halves goes to the reconcile,
    // with the original left as it is (a visible extra unit beats a lost one). An unreadable read
    // proves nothing (a 200 from add.js means both halves landed together), so it is just this
    // attempt failing: the next retries, and if none can read, the shopper is told to check.
    const started = machine.quantity;
    const remaining = started - 1;
    // Why the reduction didn't happen, for the log; the network is the default when no attempt said.
    let reason = 'no attempt could read the cart or reach change.js';
    for (let attempt = 0; attempt < 3; attempt += 1) {
      const cart = await window.bfxCart.getCart().catch((error) => {
        console.warn('Protection plan offer: could not read the cart to reduce the split', error);
        return null;
      });
      const state = bundleStateIn(cart, bundleId);
      if (state !== 'unknown' && state !== 'both') {
        const resync = await reconcileLeavesOrphan(bundleId);
        throw new AddError(`split added but the bundle is ${state}`, { cartChanged: true, resync });
      }
      const current =
        state === 'both' &&
        findMachine(cart, {
          key: machine.key,
          variantId: machine.variant_id,
          properties: machine.properties,
        });
      if (current?.quantity === remaining) return bundleId; // an earlier attempt landed
      if (current && current.quantity !== started) {
        // Changed elsewhere mid-split: reported as its own reason, not as a failed reduction.
        reason = `the original line changed to ${current.quantity} mid-split`;
        break;
      }
      if (current) {
        const res = await window.bfxCart.changeLine(current.key, remaining).catch((error) => {
          console.warn('Protection plan offer: reducing the split original failed', error);
          return null;
        });
        if (res?.ok) return bundleId;
        if (res) reason = `the reduction was refused (${res.status})`;
      }
    }

    // The bundle exists but the original still holds the moved unit, so the cart shows one
    // machine too many. Visible and removable, which beats undoing a bundle the shopper asked for.
    // Its halves weren't seen together since the last read (every read may have failed), so the
    // orphan check runs before anything renders, as on every other exit after the plan landed.
    const resync = await reconcileLeavesOrphan(bundleId);
    throw new AddError(`added the bundle but could not reduce the original line: ${reason}`, {
      cartChanged: true,
      planAdded: true,
      resync,
    });
  };

  // After the re-render the offer is gone, taking focus with it. Hand it to the new plan's Remove
  // link, whose name says which plan was added and to what, so keyboard and screen-reader users
  // land on the result rather than the top of the document. Only when nothing else claimed focus.
  const focusAddedPlan = (bundleId) => {
    requestAnimationFrame(() => {
      if (document.activeElement && document.activeElement !== document.body) return;
      const plan = [
        ...document.querySelectorAll('[data-products] .cart__item[data-is-plan="true"]'),
      ].find((line) => line.dataset.bundleId === bundleId);
      // Not into a drawer that was closed while the add ran (its Close stays live under the lock):
      // focus there is hidden, and it would stop the drawer handing focus back to its opener.
      const drawer = plan?.closest('.drawer');
      if (drawer && !drawer.classList.contains('drawer--is-open')) return;
      plan?.querySelector('.cart__remove a')?.focus();
    });
  };

  // ── Element ─────────────────────────────────

  class ProtectionPlanCartOffer extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();

      if (
        heldError?.variantId === this.dataset.variantId &&
        heldError.properties === propertiesKey(this.renderedLine()?.properties) &&
        Date.now() < heldError.until
      ) {
        this.showError(heldError.message);
        const picked = [...this.querySelectorAll('input[type="radio"]')].find(
          (input) => input.value === heldError.planId
        );
        if (picked) {
          picked.checked = true;
          this.revealActions();
        }
        // The re-render took focus away with the old offer. Bring a keyboard user back to it: to
        // Add when the pick came back, so a retry is one press, otherwise to the plans. On the next
        // frame, not now: this runs inside the add's recovery render, which the add's lock then
        // takes over (making this offer inert), and only releases when the add finishes. Focused
        // now, the target would go inert and drop focus to the page, as focusAddedPlan also waits.
        requestAnimationFrame(() => {
          if (!this.isConnected) return;
          if (document.activeElement && document.activeElement !== document.body) return;
          (picked ? this.addButton() : this.querySelector('input[type="radio"]'))?.focus();
        });
      }

      this.querySelector('.protection-plan-cart-offer__details')?.addEventListener(
        'click',
        (event) => this.onDetails(event),
        { signal: this.controller.signal }
      );

      // Picking a plan only chooses it — ordinary radio behaviour, so arrowing through the
      // options and changing your mind cost nothing. The Add button, revealed by the first pick,
      // is what adds.
      this.addEventListener(
        'change',
        (event) => {
          if (!event.target.matches('input[type="radio"]')) return;
          // A new pick supersedes a held failure, so a re-render can't put the old one back.
          heldError = null;
          this.showError('');
          this.revealActions();
        },
        { signal: this.controller.signal }
      );

      // Enter in a form control submits the form, and the cart form's default button is Check Out,
      // so Enter on a plan would send the shopper to checkout without it. Here it does what
      // submitting this group means: pick the plan and hand focus to Add, one press from adding.
      this.addEventListener(
        'keydown',
        (event) => {
          if (event.key !== 'Enter' || !event.target.matches('input[type="radio"]')) return;
          event.preventDefault();
          if (event.target.disabled) return;
          if (!event.target.checked) {
            event.target.checked = true;
            event.target.dispatchEvent(new Event('change', { bubbles: true }));
          }
          this.addButton()?.focus();
        },
        { signal: this.controller.signal }
      );

      this.addButton()?.addEventListener(
        'click',
        () => {
          const picked = this.querySelector('input[type="radio"]:checked');
          if (picked) this.addPlan(picked);
        },
        { signal: this.controller.signal }
      );
    }

    addButton() {
      return this.querySelector('.protection-plan-cart-offer__add');
    }

    // Add, and the info tooltip beside it on a line of 2+, appear with the first pick.
    revealActions() {
      const actions = this.querySelector('.protection-plan-cart-offer__actions');
      if (actions) actions.hidden = false;
    }

    // The machine line as it rendered, in the shape /cart.js gives, or null if it can't be read.
    // Liquid serialises a line's properties as [key, value] pairs, not the object /cart.js gives,
    // and spreading those pairs into a change writes keys named 0, 1… onto the order line.
    renderedLine() {
      try {
        const properties = JSON.parse(this.dataset.properties || '{}');
        return {
          key: this.dataset.lineKey,
          variant_id: Number(this.dataset.variantId),
          quantity: Number(this.dataset.quantity),
          properties: Array.isArray(properties) ? Object.fromEntries(properties) : properties,
        };
      } catch (error) {
        console.error('Protection plan offer: unreadable rendered line; reading the cart', error);
        return null;
      }
    }

    disconnectedCallback() {
      this.controller?.abort();
    }

    async onDetails(event) {
      const button = event.currentTarget;
      // Ignore repeat clicks while the first one is still loading the modal.
      if (button.getAttribute('aria-busy') === 'true') return;
      button.setAttribute('aria-busy', 'true');
      this.showError('');
      const request = {};
      requested = request;

      try {
        const instance = await loadModal(this.dataset.productUrl);
        if (requested === request) openModal(instance, button);
      } catch (error) {
        console.error('Protection plan offer: could not open the coverage details', error);
        // Otherwise the click just does nothing. The next click retries (a failed load isn't cached).
        this.showError(this.dataset.detailsError);
      } finally {
        if (requested === request) requested = null;
        button.removeAttribute('aria-busy');
      }
    }

    // While a plan is being added: the Add button shows a spinner and the adding message, the
    // radios are disabled (element.radio dims them) and the group marked busy, a hidden status
    // announces the add (a changed label isn't announced), and the rest of the cart is made
    // inert —
    // every other line, and this machine's own image and details (stepper, Remove, links) — so
    // nothing can change the cart underneath the add. A quantity change mid-add would run the
    // reconcile against a half-built bundle. Cleared when the add fails; on success the re-render
    // replaces all of it.
    setBusy(busy) {
      this.querySelectorAll('input[type="radio"]').forEach((input) => {
        input.disabled = busy;
      });
      this.querySelector('[role="radiogroup"]')?.setAttribute('aria-busy', String(busy));

      const status = this.querySelector('.protection-plan-cart-offer__status');
      if (status) status.textContent = busy ? this.dataset.addingMessage : '';

      // The button itself says what's happening: the label swaps to the adding message beside a
      // spinner. Not disabled — that drops focus to the page; addPlan's own guard is what ignores
      // a second press.
      const add = this.addButton();
      const label = add?.querySelector('.protection-plan-cart-offer__add-label');
      if (add && label) {
        if (busy) {
          add.dataset.label = label.textContent;
          label.textContent = this.dataset.addingMessage;
        } else if (add.dataset.label) {
          label.textContent = add.dataset.label;
        }
        add.classList.toggle('is-loading', busy);
        // The adding message names the machine already, so its "for …" would say it twice.
        add
          .querySelector('.protection-plan-cart-offer__add-context')
          ?.toggleAttribute('hidden', busy);
      }

      // The rest of the cart, and checkout, through the shared lock (bfx-cart-remove.js).
      if (busy) {
        this.release = window.bfxCart.lock(this.closest('[data-products]'), this);
      } else {
        this.release?.();
        this.release = null;
      }
    }

    // The add error names the plan that was picked; its placeholder is filled here because the
    // pick isn't known when the message renders.
    errorMessageFor(input, error = null) {
      let message = this.dataset.errorMessage;
      if (error?.planAdded) message = this.dataset.splitError;
      else if (error?.unconfirmed) message = this.dataset.unconfirmedError;
      return (message || '').replace('[plan]', input.dataset.planName || '');
    }

    showError(message) {
      const error = this.querySelector('.protection-plan-cart-offer__error');
      if (error) error.textContent = message;
    }

    async addPlan(input) {
      // A second press, or another line's add still running (the cart is inert then, so only
      // a press already queued gets here).
      if (adding) return;
      adding = true;
      heldError = null;
      this.setBusy(true);
      this.showError('');

      // Held for the whole add, whatever re-renders the cart meanwhile: a quantity change's
      // rebuild, a reconcile's (settled() below waits out its run), this add's own recovery render,
      // or a late one landing while the page reloads. Each replaces the lines the lock froze with
      // live ones, so the lock is taken again over the new lines as they land. A MutationObserver
      // callback is a microtask straight after the theme swaps the markup, before any click can
      // reach the new lines. Checking at points in time instead would leave the lines live between
      // a re-render and the next check.
      const surface = this.closest('[data-products]');
      const relock = surface
        ? new MutationObserver((records) => {
            const replaced = records.some((record) =>
              [...record.removedNodes].some(
                (node) => node.matches?.('.cart__item') || node.querySelector?.('.cart__item')
              )
            );
            if (!replaced) return;
            this.release?.();
            this.release = window.bfxCart.lock(surface);
          })
        : null;
      relock?.observe(surface, { childList: true });

      try {
        // Always from a fresh read of the cart, never the render: the line can change without this
        // offer re-rendering first (a quantity request still in flight, another tab or window), and
        // pairing from a stale quantity writes it back over the real one. When the theme is still
        // sending a quantity change, wait for its rebuild to land first.
        if (quantityPending(surface)) {
          // Nothing has changed yet, so the add stops with its ordinary error and the pick kept:
          // once the slow change lands, trying again is one press.
          if (!(await quantitySettled(surface))) {
            throw new AddError('a quantity change was still in flight after the wait');
          }
        }
        // Nor while the plan reconcile is running (the page-load one, or the one that change just
        // set off): it can be rebuilding this very machine, and both would then change the line.
        await window.bfxCart.settled?.();
        const rendered = this.renderedLine();
        const cart = await window.bfxCart.getCart();
        const machine = findMachine(cart, {
          key: this.dataset.lineKey,
          variantId: this.dataset.variantId,
          properties: rendered?.properties,
        });
        // Gone, or already paired: the cart changed somewhere else since this rendered.
        if (!machine) {
          throw new AddError('the machine line is no longer in the cart unpaired', {
            cartChanged: true,
          });
        }

        const bundleId =
          machine.quantity > 1
            ? await splitOffBundle(machine, input.value, this.dataset.parentSku)
            : await attachToLine(machine, input.value, this.dataset.parentSku);

        if (!(await this.rebuildOrReload())) return;
        focusAddedPlan(bundleId);
      } catch (error) {
        console.error('Protection plan offer: could not add the plan', error);
        if (error.resync) {
          // A plan with no machine is still in the cart: no re-render that would hand checkout
          // back. The reload holds the lock until the page goes, and its reconcile retries.
          this.reload('a plan was left without its machine; reloading to retry the cleanup.');
          return;
        }
        // Also when this offer is no longer on the page: a quantity change that was pending at
        // Add rebuilt the cart, replacing it, and a message written here would never be seen.
        if (error.cartChanged || !this.isConnected) {
          // Show the shopper the cart they actually have, and the message on its new offer.
          // A plan that was, or may have been, added isn't picked again, so the next press can't
          // add a second.
          holdError(
            this.dataset.variantId,
            this.renderedLine()?.properties,
            this.errorMessageFor(input, error),
            error.planAdded || error.unconfirmed ? null : input.value
          );
          // Awaited, as on success: the lock stays until the cart on screen is the real one.
          await this.rebuildOrReload();
        } else {
          // The pick stays, so trying again is one press. Held too, because a re-render can still
          // replace this offer a moment later: the slow quantity change a timed-out wait gave up
          // on lands after the add stops, and its rebuild swaps the offer out.
          const message = this.errorMessageFor(input);
          this.showError(message);
          holdError(this.dataset.variantId, this.renderedLine()?.properties, message, input.value);
        }
      } finally {
        adding = false;
        // Released even if the re-render already replaced this offer, so checkout comes back —
        // unless the page is reloading, when the lock should hold until it goes: the observer
        // stays on then, so a render that lands before the page goes is locked too. Otherwise it
        // stops before the release, so the release is the last word on the lock.
        if (!this.reloading) {
          relock?.disconnect();
          this.setBusy(false);
        }
      }
    }

    // Re-render the cart this add changed. When the render never lands (a failed fetch renders
    // nothing), the cart on screen no longer matches Shopify's, and a retry from it could add a
    // second bundle, so reload to resync, as a removal does, keeping the lock until the page goes.
    async rebuildOrReload() {
      if (await window.bfxCart.requestBuild()) return true;
      this.reload('cart rebuild did not land; reloading to resync.');
      return false;
    }

    reload(reason) {
      console.error(`Protection plan offer: ${reason}`);
      this.reloading = true;
      window.location.reload();
    }
  }

  if (!customElements.get('protection-plan-cart-offer')) {
    customElements.define('protection-plan-cart-offer', ProtectionPlanCartOffer);
  }
}
